import { Loader2, AlertCircle, Users, Home, Heart, FileText } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { useAdmissionsFormForAdmin } from './useAdmissionsFormForAdmin';

interface AdminAdmissionsReviewProps {
  familyEnrollmentId: string | undefined;
  open: boolean;
  onClose: () => void;
}

interface ParentGuardian {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  relationship?: string;
  isPrimary?: boolean;
}

interface Student {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  gradeLevel?: string;
  currentSchool?: string;
  medicalInfo?: {
    allergies?: string;
    medicalConditions?: string;
    medications?: string;
    specialNeeds?: string;
    physicianName?: string;
    physicianPhone?: string;
  };
}

interface EmergencyContact {
  name?: string;
  phone?: string;
  relationship?: string;
}

interface ChurchAffiliation {
  churchName?: string;
  pastorName?: string;
  churchEmail?: string;
  yearsAttending?: number;
}

export function AdminAdmissionsReview({ familyEnrollmentId, open, onClose }: AdminAdmissionsReviewProps) {
  const { data, loading, error } = useAdmissionsFormForAdmin(familyEnrollmentId, !open);

  const formResult = data?.admissionsFormForAdmin;
  const answers = (formResult?.formAnswers ?? {}) as Record<string, unknown>;
  const familyInfo = (answers.familyInfo ?? {}) as Record<string, unknown>;
  const parentGuardians = (answers.parentGuardians ?? []) as ParentGuardian[];
  const students = (answers.students ?? []) as Student[];
  const newStudents = (answers.newStudents ?? []) as Student[];
  const allStudents = [...students, ...newStudents];
  const emergencyContacts = (answers.emergencyContacts ?? []) as EmergencyContact[];
  const churchAffiliation = (answers.churchAffiliation ?? {}) as ChurchAffiliation;
  const address = (familyInfo.address ?? {}) as Record<string, string>;
  const submittedAt = formResult?.submittedAt ? new Date(formResult.submittedAt as string) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Admissions Form Review
          </DialogTitle>
          {submittedAt && (
            <p className="text-sm text-muted-foreground">
              Submitted {submittedAt.toLocaleDateString()} at {submittedAt.toLocaleTimeString()}
            </p>
          )}
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-destructive py-4">
            <AlertCircle className="h-5 w-5" />
            <span>Failed to load form data</span>
          </div>
        )}

        {!loading && !error && !formResult && (
          <p className="text-muted-foreground py-4">No admissions form found for this enrollment.</p>
        )}

        {formResult && (
          <div className="space-y-6 mt-2">
            {/* Family Info */}
            <Section title="Family Information" icon={<Home className="h-4 w-4" />}>
              <Field label="Family Name" value={familyInfo.familyName as string} />
              {address.street && (
                <Field
                  label="Address"
                  value={[address.street, address.street2, `${address.city}, ${address.state} ${address.zip}`]
                    .filter(Boolean)
                    .join('\n')}
                />
              )}
            </Section>

            {/* Parents/Guardians */}
            <Section title={`Parents / Guardians (${parentGuardians.length})`} icon={<Users className="h-4 w-4" />}>
              {parentGuardians.map((pg, i) => (
                <div key={i} className="rounded-md border p-3 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{pg.firstName} {pg.lastName}</span>
                    {pg.isPrimary && <Badge variant="secondary">Primary</Badge>}
                    {pg.relationship && <Badge variant="outline">{pg.relationship}</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    {pg.email && <div>Email: {pg.email}</div>}
                    {pg.phone && <div>Phone: {pg.phone}</div>}
                  </div>
                </div>
              ))}
            </Section>

            {/* Students */}
            <Section title={`Students (${allStudents.length})`} icon={<Users className="h-4 w-4" />}>
              {allStudents.map((s, i) => (
                <div key={i} className="rounded-md border p-3 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{s.firstName} {s.lastName}</span>
                    {s.gradeLevel && <Badge variant="secondary">{s.gradeLevel}</Badge>}
                    {s.gender && <Badge variant="outline">{s.gender}</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    {s.dateOfBirth && <div>DOB: {s.dateOfBirth}</div>}
                    {s.currentSchool && <div>Prior School: {s.currentSchool}</div>}
                  </div>
                  {s.medicalInfo && Object.values(s.medicalInfo).some(Boolean) && (
                    <div className="mt-2 pt-2 border-t text-sm">
                      <span className="font-medium text-xs uppercase tracking-wide text-muted-foreground">Medical</span>
                      <div className="grid grid-cols-2 gap-1 mt-1 text-muted-foreground">
                        {s.medicalInfo.allergies && <div>Allergies: {s.medicalInfo.allergies}</div>}
                        {s.medicalInfo.medicalConditions && <div>Conditions: {s.medicalInfo.medicalConditions}</div>}
                        {s.medicalInfo.medications && <div>Medications: {s.medicalInfo.medications}</div>}
                        {s.medicalInfo.specialNeeds && <div>Special Needs: {s.medicalInfo.specialNeeds}</div>}
                        {s.medicalInfo.physicianName && <div>Physician: {s.medicalInfo.physicianName}</div>}
                        {s.medicalInfo.physicianPhone && <div>Physician Phone: {s.medicalInfo.physicianPhone}</div>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </Section>

            {/* Emergency Contacts */}
            {emergencyContacts.length > 0 && (
              <Section title="Emergency Contacts">
                {emergencyContacts.map((c, i) => (
                  <div key={i} className="text-sm mb-1">
                    <span className="font-medium">{c.name}</span>
                    {c.phone && <span className="text-muted-foreground"> — {c.phone}</span>}
                    {c.relationship && <span className="text-muted-foreground"> ({c.relationship})</span>}
                  </div>
                ))}
              </Section>
            )}

            {/* Church Affiliation */}
            {Object.values(churchAffiliation).some(Boolean) && (
              <Section title="Church Affiliation" icon={<Heart className="h-4 w-4" />}>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {churchAffiliation.churchName && <Field label="Church" value={churchAffiliation.churchName} />}
                  {churchAffiliation.pastorName && <Field label="Pastor" value={churchAffiliation.pastorName} />}
                  {churchAffiliation.churchEmail && <Field label="Church Email" value={churchAffiliation.churchEmail} />}
                  {churchAffiliation.yearsAttending != null && <Field label="Years Attending" value={String(churchAffiliation.yearsAttending)} />}
                </div>
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        {icon}
        {title}
      </h4>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="mb-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className="text-sm whitespace-pre-line">{value}</p>
    </div>
  );
}
